import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, DestroyRef, OnInit, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatMenuModule } from '@angular/material/menu';
import { PageEvent } from '@angular/material/paginator';
import { MatTableModule } from '@angular/material/table';
import { Router } from '@angular/router';
import { TextPaginatorComponent } from 'app/core/components/text-paginator/text-paginator.component';
import { OverlayLoaderDirective } from 'app/core/directives/overlay-loader.directive';
import { ToastrService } from 'ngx-toastr';
import { Subject, catchError, debounceTime, distinctUntilChanged, finalize, map, of, switchMap, tap } from 'rxjs';
import {
  PHONE_DIRECTORY_SOURCE_OPTIONS,
  PhoneDirectoryEntry,
  PhoneDirectorySourceFilter,
  PhoneDirectorySourceType,
} from './phone-directory.models';
import { PhoneDirectoryService } from './phone-directory.service';

@Component({
  selector: 'app-phone-directory-dialog',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatButtonModule,
    MatDialogModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatMenuModule,
    MatTableModule,
    OverlayLoaderDirective,
    TextPaginatorComponent,
  ],
  templateUrl: './phone-directory-dialog.component.html',
  styles: `
    ::ng-deep .mat-mdc-dialog-container .mdc-dialog__surface {
      padding: 0 !important;
    }

    .phone-directory-dialog-table-card {
      box-shadow: 0 2px 8px rgb(15 23 42 / 0.08);
    }

    :host-context(.dark) .phone-directory-dialog-table-card {
      box-shadow: 0 2px 8px rgb(0 0 0 / 0.35);
    }

    .phone-directory-dialog-search.mat-mdc-form-field {
      --mdc-outlined-text-field-container-shape: 0.75rem;
    }

    .phone-directory-dialog-search ::ng-deep .mat-mdc-text-field-wrapper {
      background: transparent;
    }

    .phone-directory-dialog-table ::ng-deep .mat-mdc-header-row.phone-directory-dialog-header-row {
      background: rgb(244 244 245);
      height: 3rem;
    }

    :host-context(.dark) .phone-directory-dialog-table ::ng-deep .mat-mdc-header-row.phone-directory-dialog-header-row {
      background: rgb(39 39 42 / 0.55);
    }

    .phone-directory-dialog-table ::ng-deep .mat-mdc-header-cell {
      color: rgb(113 113 122);
      font-size: 0.75rem;
      font-weight: 600;
      letter-spacing: 0.01em;
      border-bottom: 1px solid rgb(228 228 231);
      border-right: 1px solid rgb(228 228 231);
    }

    .phone-directory-dialog-table ::ng-deep .mat-mdc-header-cell:last-child {
      border-right: none;
    }

    :host-context(.dark) .phone-directory-dialog-table ::ng-deep .mat-mdc-header-cell {
      color: rgb(161 161 170);
      border-bottom-color: rgb(63 63 70);
      border-right-color: rgb(63 63 70);
    }

    .phone-directory-dialog-table ::ng-deep .mat-mdc-row.phone-directory-dialog-data-row {
      height: 3.5rem;
      cursor: pointer;
    }

    .phone-directory-dialog-table ::ng-deep .mat-mdc-cell {
      border-bottom: 1px solid rgb(228 228 231);
      border-right: none;
      padding-top: 0.65rem;
      padding-bottom: 0.65rem;
    }

    :host-context(.dark) .phone-directory-dialog-table ::ng-deep .mat-mdc-cell {
      border-bottom-color: rgb(63 63 70);
    }
  `,
})
export class PhoneDirectoryDialogComponent implements OnInit {
  private readonly destroyRef = inject(DestroyRef);
  private readonly api = inject(PhoneDirectoryService);
  private readonly router = inject(Router);
  private readonly toast = inject(ToastrService);
  private readonly dialogRef = inject(MatDialogRef<PhoneDirectoryDialogComponent>);
  private readonly keywordSearch$ = new Subject<string>();
  private readonly reload$ = new Subject<'page' | 'silent'>();

  readonly sourceOptions = PHONE_DIRECTORY_SOURCE_OPTIONS;
  readonly displayedColumns = ['type', 'name', 'related', 'code', 'phone', 'mobile', 'email', 'role'];

  keyword = '';
  source: PhoneDirectorySourceFilter = 'all';
  pageLoader = false;
  rows: PhoneDirectoryEntry[] = [];
  totalRecords = 0;
  pageSize = 20;
  pageIndex = 0;
  private loadedKeyword = '';

  get sourceLabelFilter(): string {
    return this.sourceOptions.find((option) => option.value === this.source)?.label ?? 'All';
  }

  get emptyTitle(): string {
    const keyword = this.keyword.trim();
    if (keyword.length < 2) return 'Type at least 2 characters to search';
    if (this.pageLoader || this.loadedKeyword !== keyword) return 'Searching…';
    return 'No matches found';
  }

  get emptyIcon(): string {
    return this.emptyTitle === 'No matches found' ? 'search_off' : 'search';
  }

  ngOnInit(): void {
    this.keywordSearch$
      .pipe(debounceTime(300), distinctUntilChanged(), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.pageIndex = 0;
        this.reload$.next('silent');
      });

    this.reload$
      .pipe(
        switchMap((loader) => this.fetch(loader)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  close(): void {
    this.dialogRef.close();
  }

  setSource(value: PhoneDirectorySourceFilter): void {
    if (this.source === value) return;
    this.source = value;
    this.pageIndex = 0;
    this.rows = [];
    this.totalRecords = 0;
    this.loadedKeyword = '';
    this.reload$.next('page');
  }

  onKeywordChange(): void {
    const keyword = this.keyword.trim();
    if (keyword.length < 2) {
      this.pageIndex = 0;
      this.rows = [];
      this.totalRecords = 0;
      this.pageLoader = false;
      this.loadedKeyword = '';
    }
    this.keywordSearch$.next(keyword);
  }

  clearKeyword(): void {
    this.keyword = '';
    this.pageIndex = 0;
    this.rows = [];
    this.totalRecords = 0;
    this.pageLoader = false;
    this.loadedKeyword = '';
    this.keywordSearch$.next('');
  }

  onPageChange(event: PageEvent): void {
    this.pageIndex = event.pageIndex;
    this.pageSize = event.pageSize;
    this.reload$.next('page');
  }

  openRow(row: PhoneDirectoryEntry): void {
    if (!row.navigateId) return;
    this.dialogRef.close();
    if (row.navigateTo === 'employee') {
      void this.router.navigate(['/hr/employees/detail', row.navigateId]);
      return;
    }
    if (row.navigateTo === 'business-partner') {
      void this.router.navigate(['/admin/business-partners/detail', row.navigateId]);
    }
  }

  sourceLabel(sourceType: PhoneDirectorySourceType | string): string {
    switch (sourceType) {
      case 'employee':
        return 'Employee';
      case 'businessPartner':
        return 'Business partner';
      case 'contact':
        return 'Contact';
      default:
        return sourceType || '—';
    }
  }

  sourceBadgeClass(sourceType: PhoneDirectorySourceType | string): string {
    switch (sourceType) {
      case 'employee':
        return 'bg-primary/10 text-primary';
      case 'businessPartner':
        return 'bg-amber-500/15 text-amber-700 dark:text-amber-300';
      case 'contact':
        return 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300';
      default:
        return 'bg-slate-200 text-slate-700 dark:bg-zinc-700 dark:text-zinc-200';
    }
  }

  cell(value: string | null | undefined): string {
    const trimmed = value?.trim();
    return trimmed ? trimmed : '—';
  }

  private fetch(loader: 'page' | 'silent') {
    const keyword = this.keyword.trim();
    if (keyword.length < 2) {
      this.rows = [];
      this.totalRecords = 0;
      this.pageLoader = false;
      this.loadedKeyword = '';
      return of(undefined);
    }

    if (loader === 'page' || this.rows.length === 0) this.pageLoader = true;

    return this.api
      .search({
        keyword,
        source: this.source,
        pageNumber: this.pageIndex + 1,
        pageSize: this.pageSize,
      })
      .pipe(
        tap((page) => {
          this.rows = page.items;
          this.totalRecords = page.totalRecords;
          this.loadedKeyword = keyword;
        }),
        catchError((err: HttpErrorResponse) => {
          this.rows = [];
          this.totalRecords = 0;
          this.loadedKeyword = keyword;
          const body = err.error as { message?: string } | null;
          this.toast.error(body?.message || 'Unable to load phone directory');
          return of({ items: [] as PhoneDirectoryEntry[], totalRecords: 0 });
        }),
        finalize(() => {
          this.pageLoader = false;
        }),
        map(() => undefined)
      );
  }
}
